/**
 * Show Normalizer
 *
 * Transforms CslItem into a flat, agent-friendly normalized structure
 * for JSON output and as data source for pretty formatting.
 */

import path from "node:path";
import type { CslItem } from "../../core/csl-json/types.js";
import { buildUuidIndex, getSupersededMark, resolveFinalSuccessor } from "../superseded/index.js";

/** Convert OS path separators to POSIX forward slashes for consistent output. */
function toPosixPath(p: string): string {
  return p.split(path.sep).join("/");
}

export interface NormalizedFulltext {
  pdf: string | null;
  markdown: string | null;
}

export interface NormalizedAttachment {
  filename: string;
  role: string;
}

/** Resolved successor pointer (#108). */
export interface NormalizedSuperseded {
  /** Citation key of the successor, or null when the pointer dangles */
  id: string | null;
  /** uuid the pointer names, kept so a dangling pointer is still traceable */
  uuid: string;
  reason: string;
  at: string | null;
  /** True when the successor chain loops; the library was edited outside `ref deprecate` */
  cycle: boolean;
}

export interface NormalizedReference {
  id: string;
  uuid: string | null;
  type: string;
  title: string | null;
  authors: string[] | null;
  year: number | null;
  journal: string | null;
  volume: string | null;
  issue: string | null;
  page: string | null;
  doi: string | null;
  pmid: string | null;
  pmcid: string | null;
  url: string | null;
  abstract: string | null;
  tags: string[] | null;
  created: string | null;
  modified: string | null;
  fulltext: NormalizedFulltext | null;
  attachments: NormalizedAttachment[] | null;
  /** Null when the reference is not superseded, or when no library was supplied to resolve against */
  superseded: NormalizedSuperseded | null;
  raw: CslItem;
}

export interface NormalizeOptions {
  attachmentsDirectory?: string;
  /**
   * The whole library, needed to turn a `superseded_by` uuid back into a citation key.
   * Omit it and `superseded` stays null.
   */
  allItems?: CslItem[];
}

function formatAuthor(author: {
  family?: string | undefined;
  given?: string | undefined;
  literal?: string | undefined;
}): string {
  if (author.literal) return author.literal;
  const family = author.family || "";
  const givenInitial = author.given ? `${author.given.charAt(0)}.` : "";
  return givenInitial ? `${family}, ${givenInitial}` : family;
}

function resolveFulltextAndAttachments(
  item: CslItem,
  attachmentsDirectory: string
): { fulltext: NormalizedFulltext; attachments: NormalizedAttachment[] } {
  const attachments = item.custom?.attachments;
  if (!attachments) {
    return {
      fulltext: { pdf: null, markdown: null },
      attachments: [],
    };
  }

  const dir = path.join(attachmentsDirectory, attachments.directory);
  const files = attachments.files ?? [];

  let pdfPath: string | null = null;
  let markdownPath: string | null = null;
  const nonFulltext: NormalizedAttachment[] = [];

  for (const file of files) {
    if (file.role === "fulltext") {
      if (file.filename.endsWith(".pdf")) {
        pdfPath = toPosixPath(path.join(dir, file.filename));
      } else if (file.filename.endsWith(".md")) {
        markdownPath = toPosixPath(path.join(dir, file.filename));
      }
    } else {
      nonFulltext.push({ filename: file.filename, role: file.role });
    }
  }

  return {
    fulltext: { pdf: pdfPath, markdown: markdownPath },
    attachments: nonFulltext,
  };
}

function normalizeAuthors(item: CslItem): string[] | null {
  return item.author && item.author.length > 0 ? item.author.map(formatAuthor) : null;
}

function normalizeFileInfo(
  item: CslItem,
  options?: NormalizeOptions
): { fulltext: NormalizedFulltext | null; attachments: NormalizedAttachment[] | null } {
  if (!options?.attachmentsDirectory) {
    return { fulltext: null, attachments: null };
  }
  return resolveFulltextAndAttachments(item, options.attachmentsDirectory);
}

function normalizeSuperseded(
  item: CslItem,
  options?: NormalizeOptions
): NormalizedSuperseded | null {
  const mark = getSupersededMark(item);
  if (!mark || !options?.allItems) {
    return null;
  }
  const chain = resolveFinalSuccessor(item, buildUuidIndex(options.allItems));
  return {
    id: chain?.target?.id ?? null,
    uuid: mark.supersededBy,
    reason: mark.reason,
    at: mark.at ?? null,
    cycle: chain?.cycle ?? false,
  };
}

export function normalizeReference(item: CslItem, options?: NormalizeOptions): NormalizedReference {
  const custom = item.custom;
  const { fulltext, attachments } = normalizeFileInfo(item, options);

  return {
    id: item.id,
    uuid: custom?.uuid ?? null,
    type: item.type,
    title: item.title ?? null,
    authors: normalizeAuthors(item),
    year: item.issued?.["date-parts"]?.[0]?.[0] ?? null,
    journal: (item["container-title"] as string) ?? null,
    volume: item.volume ?? null,
    issue: item.issue ?? null,
    page: item.page ?? null,
    doi: item.DOI ?? null,
    pmid: item.PMID ?? null,
    pmcid: item.PMCID ?? null,
    url: item.URL ?? null,
    abstract: item.abstract ?? null,
    tags: custom?.tags ?? null,
    created: custom?.created_at ?? null,
    modified: custom?.timestamp ?? null,
    fulltext,
    attachments,
    superseded: normalizeSuperseded(item, options),
    raw: item,
  };
}
